'use client';

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { CaretLeft, CaretRight, Check } from '@/components/icons';
import { Sheet } from './sheet';
import { useViewport } from './use-viewport';
import styles from './menu.module.css';
import { useT } from '@/lib/locale-context';

/**
 * Menu - the design-system dropdown/overflow menu. A trigger (render-prop, so
 * it can be a kebab IconButton, a button, anything) plus a popover list of typed
 * items: actions, a `danger` action, a non-interactive `label` (a caption) or
 * `note` (content in the row voice), and a `separator`.
 *
 * **Positioning:** Floating UI `position: fixed` (the same strategy used by
 * `useAnchored`): the panel escapes any scrollable ancestor's clip; `flip()`
 * opens upward when cramped, `shift()` keeps it in-viewport. The positioning
 * is inlined here rather than delegated to `useAnchored` because Menu owns
 * three refs that must all share the same DOM nodes (wrapper, menu panel) and
 * the `react-hooks/immutability` rule prohibits writing to refs returned from
 * custom hooks.
 *
 * **Keyboard (WAI-ARIA menu pattern, desktop popover):**
 * - Opening moves focus to the first item automatically.
 * - ArrowDown / ArrowUp move focus between items (wrapping at boundaries).
 * - Home / End jump to the first / last item.
 * - Escape closes and restores focus to the trigger, and claims the key's
 *   default, so a native dialog around the menu stays open.
 * - Enter / Space activate the focused item (native button behaviour).
 * - Tab closes the menu and lets Tab move naturally past the trigger.
 *
 * On compact viewports the same items render in a bottom `Sheet` instead of
 * the anchored popover (docs/MOBILE.md §4). The Sheet's native `<dialog>`
 * provides its own focus trap and focus-restoration; arrow-key nav is omitted.
 * `anchoredOnCompact` keeps the anchored popover on compact too, for the one
 * case the sheet rules name: a short values menu inside an overlay, where a
 * Sheet would open over a sheet.
 *
 * **The drill-in** (docs/MOBILE.md "THE SHEET RULES", built 2026-11-02 for
 * the item page's "Add a section"): a `drill` row wears a chevron, and a tap
 * on it replaces the menu's rows with its own list, under a back row and the
 * row's label as a caption, in the same sheet or popover. One list of choices
 * is on screen at a time, and no sheet ever opens over a sheet. Back returns
 * to the outer list; closing the menu forgets the drill.
 *
 * Consumer API (trigger render-prop + MenuItem shape) is unchanged.
 */

export interface MenuActionItem {
  kind?: 'item';
  label: ReactNode;
  onSelect: () => void;
  icon?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  danger?: boolean;
  /**
   * A second line under the label: what the row COSTS, or why it is locked
   * (DESIGN_SYSTEM.md "Where an act lives", rule 4 - prices and consequences
   * ride the row, explanations ride tooltips). Quiet and wrapping, so colour
   * is never the only thing marking the way down.
   */
  description?: ReactNode;
  /**
   * The row's EXPLANATION, as the native tooltip on desktop (rule 4:
   * explanations ride tooltips, prices ride the sub-line). Dropped on compact:
   * a phone shows no tooltip, so nothing may depend on it.
   */
  title?: string;
  /** Trailing element that replaces the selected-check (e.g. a sort-direction
   *  arrow). When set, the `selected` check is not rendered. */
  accessory?: ReactNode;
  /**
   * The VALUE a value row wears (the `Notify me…` pattern, DESIGN_SYSTEM.md
   * "Where an act lives" rule 3): the current choice, quiet, and a chevron
   * that says its values open as their own menu. Takes the accessory slot, so
   * it is part of the row's name ("Appearance Auto").
   */
  value?: ReactNode;
  /** A test hook on this row, for an act an e2e spec drives by name. */
  testId?: string;
}
/**
 * A non-interactive row in the ROW voice (2026-10-05): what a sheet says before
 * its acts - who set a state, when, the statement it cites as values, a note.
 * A `label` row is a group's caption in the mono label voice; a `note` row is
 * content, at the row's own size, and never focusable.
 */
export interface MenuNoteItem {
  kind: 'note';
  label: ReactNode;
  testId?: string;
}
/**
 * A row that opens a list of its own INSIDE the menu (the drill-in): the
 * row's look is an action row's with a chevron at its end, and its `items`
 * replace the menu's rows while it is open.
 */
export interface MenuDrillItem {
  kind: 'drill';
  label: ReactNode;
  icon?: ReactNode;
  items: MenuItem[];
  disabled?: boolean;
  testId?: string;
}
export type MenuItem =
  | MenuActionItem
  | MenuNoteItem
  | MenuDrillItem
  | { kind: 'separator' }
  | { kind: 'label'; label: ReactNode };

/** What a list renders: the menu's rows, or a drill-in's under its back row. */
export type ListItem = MenuItem | { kind: 'back' };

/**
 * The rows on screen for a drill position: the menu's own, or the opened
 * drill row's list under a back row and its label as a caption.
 */
export function drilled(items: MenuItem[], at: number | null): ListItem[] {
  const row = at === null ? undefined : items[at];
  if (!row || row.kind !== 'drill') return items;
  return [{ kind: 'back' }, { kind: 'label', label: row.label }, ...row.items];
}

function MenuList({
  items,
  compact,
  onClose = () => {},
  onAfterSelect = () => {},
  onDrill = () => {},
  tabbableFirst = false,
}: {
  items: ListItem[];
  /** The rows render in a compact substrate: no tooltips. */
  compact: boolean;
  onClose?: () => void;
  /** Called after an item activates so the opener can restore focus. */
  onAfterSelect?: () => void;
  /** Open a drill row's list (its index in `items`), or go back (null). */
  onDrill?: (at: number | null) => void;
  /** Make the first enabled row a Tab stop (rows outside a popover, where no
   *  opening focus lands on them; `MenuRows`). */
  tabbableFirst?: boolean;
}) {
  const t = useT();
  const firstEnabled = tabbableFirst
    ? items.findIndex(
        (it) =>
          it.kind === 'back' ||
          ((it.kind === undefined || it.kind === 'item' || it.kind === 'drill') && !it.disabled),
      )
    : -1;
  return (
    <>
      {items.map((it, i) => {
        if (it.kind === 'separator') {
          return <div key={i} className={styles.separator} role="separator" />;
        }
        if (it.kind === 'label') {
          return (
            <div key={i} className={styles.menuLabel}>
              {it.label}
            </div>
          );
        }
        if (it.kind === 'note') {
          return (
            <div key={i} className={styles.menuNote} data-testid={it.testId}>
              {it.label}
            </div>
          );
        }
        if (it.kind === 'back') {
          return (
            <MenuActionRow
              key={i}
              item={{
                label: t('kit.menu.back'),
                icon: <CaretLeft aria-hidden />,
                testId: 'menu-back',
                onSelect: () => onDrill(null),
              }}
              compact={compact}
              tabbable={i === firstEnabled}
              onActivate={() => onDrill(null)}
            />
          );
        }
        if (it.kind === 'drill') {
          return (
            <MenuActionRow
              key={i}
              item={{
                label: it.label,
                icon: it.icon,
                disabled: it.disabled,
                testId: it.testId,
                accessory: (
                  <span className={styles.value}>
                    <CaretRight className={styles.chevron} aria-hidden />
                  </span>
                ),
                onSelect: () => onDrill(i),
              }}
              compact={compact}
              tabbable={i === firstEnabled}
              onActivate={() => onDrill(i)}
            />
          );
        }
        return (
          <MenuActionRow
            key={i}
            item={it}
            compact={compact}
            tabbable={i === firstEnabled}
            onActivate={() => {
              it.onSelect();
              onClose();
              onAfterSelect();
            }}
          />
        );
      })}
    </>
  );
}

/**
 * One action row. A row with a sub-line names itself by its LABEL (plus its
 * accessory) and carries the sub-line as its DESCRIPTION: without the split
 * the name is computed from all of the button's text, so a screen reader
 * announced "Blocked Pages parties and subscribers." as one name, and a
 * `getByRole('menuitem', { name })` could reach a row by its price. A row
 * without a sub-line keeps the name from its content.
 */
function MenuActionRow({
  item: it,
  compact,
  onActivate,
  tabbable = false,
}: {
  item: MenuActionItem;
  compact: boolean;
  onActivate: () => void;
  tabbable?: boolean;
}) {
  const id = useId();
  const labelId = `${id}-label`;
  const accessoryId = `${id}-accessory`;
  const descId = `${id}-desc`;
  const described = Boolean(it.description);
  const accessory =
    it.accessory ??
    (it.value !== undefined ? (
      <span className={styles.value}>
        {it.value}
        <CaretRight className={styles.chevron} aria-hidden />
      </span>
    ) : undefined);
  return (
    <button
      type="button"
      role="menuitem"
      className={styles.item}
      data-danger={it.danger || undefined}
      data-testid={it.testId}
      title={compact ? undefined : it.title}
      disabled={it.disabled}
      tabIndex={tabbable ? 0 : -1}
      aria-labelledby={described ? (accessory ? `${labelId} ${accessoryId}` : labelId) : undefined}
      aria-describedby={described ? descId : undefined}
      onClick={onActivate}
    >
      {it.icon && <span className={styles.icon}>{it.icon}</span>}
      <span className={styles.itemText}>
        <span className={styles.itemLabel} id={labelId}>
          {it.label}
        </span>
        {described && (
          <span className={styles.itemDesc} id={descId}>
            {it.description}
          </span>
        )}
      </span>
      {accessory ? (
        <span className={styles.accessory} id={accessoryId}>
          {accessory}
        </span>
      ) : (
        it.selected && <Check className={styles.check} aria-hidden />
      )}
    </button>
  );
}

/**
 * THE KIT'S ROW GRAMMAR, outside a popover: the same rows a `Menu` renders, for
 * a surface that owns its own substrate - the conclusion question's Dialog on
 * desktop and Sheet on compact (docs/RESOLUTION.md §6). "A sheet's content is
 * kit rows" (.claude/rules/ui-kit.md); this is how one gets them without
 * pretending to be a dropdown.
 *
 * `dense` keeps the popover's row measure instead of the sheet's tall rows,
 * for rows that sit in a desktop Dialog among other rows of that measure (the
 * item's Views picker, 2026-10-21); a sheet keeps the default.
 *
 * Keyboard (2026-10-21): the first enabled row is a Tab stop and the arrow
 * keys, Home and End move between the rows, the menu pattern the popover
 * `Menu` follows; until then the rows took no keyboard focus at all.
 */
export function MenuRows({
  items,
  ariaLabel,
  testId,
  dense = false,
}: {
  items: MenuItem[];
  ariaLabel?: string;
  testId?: string;
  dense?: boolean;
}) {
  const { compact } = useViewport();
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const rows = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])'),
    );
    if (rows.length === 0) return;
    const idx = rows.indexOf(document.activeElement as HTMLButtonElement);
    const to =
      e.key === 'ArrowDown'
        ? rows[idx < rows.length - 1 ? idx + 1 : 0]
        : e.key === 'ArrowUp'
          ? rows[idx > 0 ? idx - 1 : rows.length - 1]
          : e.key === 'Home'
            ? rows[0]
            : e.key === 'End'
              ? rows[rows.length - 1]
              : undefined;
    if (!to) return;
    e.preventDefault();
    to.focus();
  };
  return (
    <div
      className={dense ? styles.denseRows : styles.sheetMenu}
      role="menu"
      aria-label={ariaLabel}
      data-testid={testId}
      onKeyDown={onKeyDown}
    >
      <MenuList items={items} compact={compact} tabbableFirst />
    </div>
  );
}

export function Menu({
  trigger,
  items,
  align = 'end',
  ariaLabel,
  contentTestId,
  open: controlledOpen,
  onOpenChange,
  detached = false,
  anchoredOnCompact = false,
  head,
}: {
  trigger: (api: { open: boolean; toggle: () => void }) => ReactNode;
  items: MenuItem[];
  /**
   * What the menu is ABOUT, above its rows (2026-10-18, the profile menu's
   * head: who I am, and a state I wear). Not a row: it carries no row voice
   * and the opening focus skips it, though an act inside it (a `menuitem`
   * such as the worn override's End) stays on the arrow keys' path.
   */
  head?: ReactNode;
  align?: 'start' | 'end';
  ariaLabel?: string;
  /** A test hook on the list container (the sheet's rows on compact, the
   *  popover's on desktop), for a spec that scopes its queries to one menu. */
  contentTestId?: string;
  /**
   * CONTROLLED open state (2026-09-21): pass it when something other than this
   * menu's own trigger decides whether it is up - the item's meta register
   * opens its `Notify me…` value menu one frame after the register itself
   * closes (the `afterClose` pattern). Omit it and the menu owns its state as
   * before.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * The wrapper takes NO space in its parent's flow (`position: absolute` at
   * its static position) while staying the popover's anchor. For a controlled
   * menu whose `trigger` renders nothing: without it a zero-size wrapper still
   * counts as a flex item and widens the cluster it is mounted in by one gap.
   */
  detached?: boolean;
  /**
   * The anchored popover on compact too, instead of the Sheet: for a SHORT
   * values menu that opens inside an overlay (a criteria chip in the custom
   * groups Dialog), where a Sheet would open over a sheet, whose rows take no
   * taps on iOS (docs/MOBILE.md, the sheet rules). Two rows cannot clip.
   */
  anchoredOnCompact?: boolean;
}) {
  const t = useT();
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlledOpen ?? uncontrolled;
  // Wrapper div: click-outside root AND Floating UI anchor.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // Menu panel: Floating UI floating element AND keyboard query root.
  const menuRef = useRef<HTMLDivElement | null>(null);
  // The element focused before the menu opened; restored on every close path.
  const triggerFocusRef = useRef<HTMLElement | null>(null);

  const { compact: compactViewport } = useViewport();
  const compact = compactViewport && !anchoredOnCompact;
  // The open drill row (its index in `items`), or null for the menu's own rows.
  const [drillAt, setDrillAt] = useState<number | null>(null);
  // A closed menu forgets its drill (the render-time adjustment pattern), so
  // it opens on its own rows again.
  if (!open && drillAt !== null) setDrillAt(null);
  const shown = drilled(items, drillAt);
  // Moving between the lists replaces the rows under the focus: the first row
  // of the new list takes it (the back row, or the drill row's first entry).
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const drill = (at: number | null) => {
    setDrillAt(at);
    requestAnimationFrame(() => {
      const root = compact ? sheetRef.current : menuRef.current;
      root?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')?.focus();
    });
  };

  const setOpen = useCallback(
    (next: boolean) => {
      if (controlledOpen === undefined) setUncontrolled(next);
      onOpenChange?.(next);
    },
    [controlledOpen, onOpenChange],
  );
  const close = useCallback(() => setOpen(false), [setOpen]);
  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

  // Capture the trigger's focus in an effect (runs before the focus-first-item
  // RAF, so document.activeElement is still the trigger when this fires).
  useEffect(() => {
    if (open) {
      triggerFocusRef.current = document.activeElement as HTMLElement | null;
    }
  }, [open]);

  // Click-outside and Escape - dismiss + restore focus (same pattern as usePopover).
  useEffect(() => {
    if (!open) return;
    const dismiss = () => {
      setOpen(false);
      triggerFocusRef.current?.focus();
    };
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) dismiss();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);

  // Floating UI positioning - position: fixed so no scrollable ancestor clips
  // the panel (the same strategy used by useAnchored / Select / EntityField).
  useLayoutEffect(() => {
    const anchor = wrapRef.current;
    const floating = menuRef.current;
    if (!open || !anchor || !floating) return;
    return autoUpdate(anchor, floating, () => {
      void computePosition(anchor, floating, {
        strategy: 'fixed',
        placement: align === 'end' ? 'bottom-end' : 'bottom-start',
        middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        floating.style.left = `${x}px`;
        floating.style.top = `${y}px`;
      });
    });
  }, [open, align]);

  // Move focus to the first item when the popover opens. RAF defers until React
  // has flushed the menu div into the DOM and the browser has laid it out.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      // The first ROW: an act in the head is reachable, never the opening focus.
      const first = Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [],
      ).find((el) => !el.closest('[data-menu-head]'));
      first?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  // WAI-ARIA menu keyboard pattern for the desktop anchored popover.
  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const menuItems = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ??
        [],
    );
    if (!menuItems.length) return;
    const idx = menuItems.indexOf(document.activeElement as HTMLButtonElement);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = idx < menuItems.length - 1 ? menuItems[idx + 1] : menuItems[0];
        next?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = idx > 0 ? menuItems[idx - 1] : menuItems[menuItems.length - 1];
        prev?.focus();
        break;
      }
      case 'Home':
        e.preventDefault();
        menuItems[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        menuItems[menuItems.length - 1]?.focus();
        break;
      case 'Tab':
        // Tab escapes the menu; close it and restore focus so the subsequent
        // Tab movement lands on the element naturally after the trigger.
        close();
        triggerFocusRef.current?.focus();
        break;
      case 'Escape':
        // The open menu owns Esc (the document listener above closes it).
        // Inside a native dialog (a filter chip's menu in the custom groups
        // Dialog) the keydown's default is that dialog's cancel: one Esc
        // would close the menu AND the dialog.
        e.preventDefault();
        break;
      // Enter and Space activate the focused button natively (no case needed).
    }
  };

  // Restore focus after item activation. close() does NOT fire the Escape/
  // click-outside dismiss path, so focus must be restored here explicitly.
  const handleAfterSelect = () => {
    triggerFocusRef.current?.focus();
  };

  const headNode = head ? (
    <div className={styles.head} data-menu-head>
      {head}
    </div>
  ) : null;

  return (
    <div className={detached ? `${styles.wrap} ${styles.detached}` : styles.wrap} ref={wrapRef}>
      {trigger({ open, toggle })}
      {compact ? (
        <Sheet open={open} onClose={close} label={ariaLabel ?? t('kit.menu.label')}>
          <div
            ref={sheetRef}
            className={styles.sheetMenu}
            role="menu"
            aria-label={ariaLabel}
            data-testid={contentTestId}
          >
            {drillAt === null && headNode}
            <MenuList
              items={shown}
              compact
              onClose={close}
              onAfterSelect={handleAfterSelect}
              onDrill={drill}
            />
          </div>
        </Sheet>
      ) : (
        open && (
          <div
            ref={menuRef}
            className={styles.menu}
            role="menu"
            aria-label={ariaLabel}
            data-testid={contentTestId}
            onKeyDown={handleKeyDown}
          >
            {drillAt === null && headNode}
            <MenuList
              items={shown}
              compact={compactViewport}
              onClose={close}
              onAfterSelect={handleAfterSelect}
              onDrill={drill}
            />
          </div>
        )
      )}
    </div>
  );
}
